import { useState } from 'react';
import { Sparkles, Loader2, Quote } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { api, ApiError } from '@/lib/api';
import { cn } from '@/lib/utils';

interface AskSource {
  text: string;
  score: number | null;
  metadata: Record<string, unknown>;
}

interface AskResponse {
  job_id: string;
  answer: string;
  sources: AskSource[];
}

interface AskContractPanelProps {
  jobId: string;
  className?: string;
}

/**
 * "Ask the contract" — RAG question/answer panel grounded in the contract
 * text via the platform's per-app LlamaIndex collection. Answer is followed
 * by source chunks the model retrieved, so reviewers can verify the AI's
 * claim against the original document text.
 */
export function AskContractPanel({ jobId, className }: AskContractPanelProps) {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [answer, setAnswer] = useState<AskResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleAsk(e: React.FormEvent) {
    e.preventDefault();
    if (!query.trim() || loading) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await api.post<AskResponse>(`/api/v1/jobs/${jobId}/rag/query`, {
        query: query.trim(),
        top_k: 5,
      });
      setAnswer(resp);
    } catch (err: unknown) {
      const msg = err instanceof ApiError
        ? err.message
        : 'Could not query the contract. The RAG service may be unavailable.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Card className={cn(className)}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" aria-hidden="true" />
          Ask the contract
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Ask a natural-language question. Answers are grounded in the original
          contract text via LlamaIndex (Ollama, llama3.1:8b). Sources are
          shown so you can verify each claim.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <form onSubmit={(e) => { void handleAsk(e); }} className="flex gap-2">
          <input
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); }}
            placeholder='e.g. "What is the termination notice period?"'
            disabled={loading}
            className="flex-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="Question about the contract"
          />
          <Button type="submit" size="sm" disabled={loading || !query.trim()}>
            {loading
              ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              : 'Ask'}
          </Button>
        </form>

        {error && (
          <p role="alert" className="text-xs text-destructive">{error}</p>
        )}

        {answer && (
          <div className="space-y-3">
            <div className="rounded-md border border-border bg-muted/40 px-3 py-2.5">
              <p className="text-xs text-muted-foreground mb-1 uppercase tracking-wide">Answer</p>
              <p className="text-sm whitespace-pre-wrap">{answer.answer || '(no answer returned)'}</p>
            </div>

            {answer.sources.length > 0 && (
              <div>
                <p className="text-xs text-muted-foreground mb-1 uppercase tracking-wide">
                  Sources from the contract
                </p>
                <ul className="space-y-2" aria-label="Retrieved source passages">
                  {answer.sources.map((s, i) => (
                    <li key={i} className="rounded border border-border bg-background px-3 py-2">
                      <div className="flex items-start gap-2">
                        <Quote className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" aria-hidden="true" />
                        <p className="text-xs text-foreground leading-relaxed">{s.text}</p>
                      </div>
                      {s.score != null && (
                        <p className="text-[10px] text-muted-foreground mt-1 ml-5">
                          relevance {(s.score * 100).toFixed(0)}%
                          {typeof s.metadata.chunk_index === 'number'
                            ? ` · chunk #${String(s.metadata.chunk_index)}`
                            : ''}
                        </p>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
